import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { processSteps } from "../../data/content";
import { ArrowUpRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const HowWeWork = () => {
  const { isAr, t } = useLang();
  const [active, setActive] = useState(0);
  const step = processSteps[active];

  return (
    <section
      id="process"
      className="relative bg-bg-ivory py-[120px] md:py-[180px]"
      data-testid="process-section"
    >
      <div className="max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex justify-between items-center text-brand-green/65 mb-16">
          <span className="page-meta">— 18</span>
          <span className="page-meta">{t("آلية العمل", "How We Work")}</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease }}
          className="mb-16 max-w-3xl"
        >
          <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/55 mb-5`}>
            {t("[ آلية العمل ]", "[ HOW WE WORK ]")}
          </div>
          <h3
            className={`${isAr ? "arabic-title" : "serif-title"} text-brand-green mb-6`}
            style={{ fontSize: isAr ? "clamp(38px, 4.6vw, 64px)" : "clamp(46px, 5.2vw, 92px)" }}
          >
            {isAr ? "آلية عملنا" : (<>How <em className="font-light">We Work</em></>)}
          </h3>
          {isAr ? (
            <p className="arabic-title text-brand-green/75 max-w-md" dir="rtl" style={{ fontSize: 15.5, lineHeight: 1.95, fontWeight: 400 }}>
              منهجية تنفيذ منضبطة تضمن جودة وموثوقية في كل مرحلة من مراحل المشروع.
            </p>
          ) : (
            <p className="text-brand-green/75 max-w-md" style={{ fontSize: 15.5, lineHeight: 1.8 }}>
              A disciplined execution methodology that ensures quality and reliability at every stage of your project.
            </p>
          )}
        </motion.div>

        <div className="editorial-rule mb-12" />

        <div className="grid grid-cols-12 gap-x-10 gap-y-10">
          <div className="col-span-12 md:col-span-5 lg:col-span-4">
            <div className="space-y-0">
              {processSteps.map((p, i) => (
                <button
                  key={p.code}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`w-full text-start cursor-hover py-5 px-4 border-b transition-all duration-700 ${
                    active === i ? "bg-[#173F35] text-bg-ivory" : "text-brand-green"
                  }`}
                  style={{ borderColor: "rgba(23,63,53,0.15)" }}
                  data-testid={`process-step-${p.code}`}
                >
                  <div className="grid grid-cols-12 gap-3 items-center">
                    <div className={`col-span-2 page-meta ${active === i ? "text-champagne" : "text-brand-green/60"}`}>
                      {p.code}
                    </div>
                    <div className="col-span-9">
                      <div
                        className={`${isAr ? "arabic-title" : ""} ${active === i ? "text-bg-ivory" : "text-brand-green"}`}
                        style={{ fontSize: 14.5, fontWeight: 500, lineHeight: 1.45 }}
                        dir={isAr ? "rtl" : "ltr"}
                      >
                        {isAr ? p.ar : p.en}
                      </div>
                    </div>
                    <div className="col-span-1">
                      <ArrowUpRight size={14} className={`${active === i ? "text-champagne" : "text-brand-green/40"} flip-x`} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 lg:col-span-8">
            <div className="md:sticky md:top-[120px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step.code}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease }}
                  className="relative overflow-hidden h-[500px] md:h-[640px]"
                >
                  <img
                    src={step.image}
                    alt={isAr ? step.ar : step.en}
                    className="w-full h-full object-cover"
                    style={{ filter: "saturate(0.88)" }}
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,47,41,0.05), rgba(15,47,41,0.55))" }} />
                  <div className="absolute top-6 left-6 right-6 flex justify-between text-bg-ivory">
                    <span className="page-meta">— Step {step.code} / 07</span>
                    <span className="label-meta">PROCESS</span>
                  </div>
                  <div className="absolute bottom-8 left-8 right-8 text-bg-ivory">
                    <div className="font-serif-display" style={{ fontSize: 110, lineHeight: 0.85, color: "#E8D3C3" }}>
                      {step.code}
                    </div>
                    <div
                      className={`${isAr ? "arabic-title" : "serif-title italic font-light"} text-bg-ivory mt-3`}
                      style={{ fontSize: isAr ? 26 : 32, fontWeight: isAr ? 500 : 300 }}
                      dir={isAr ? "rtl" : "ltr"}
                    >
                      {isAr ? step.ar : step.en}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;
